import { Injectable, effect, inject, signal } from '@angular/core';
import { LOG_LEVEL, PURCHASES_ERROR_CODE, Purchases, type CustomerInfo, type PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { IAP_CHECK_PRODUCTS, IAP_ENTITLEMENT, IS_IOS_NATIVE, REVENUECAT_IOS_KEY } from '../env';
import { AuthService } from '../services/auth.service';
import { AnalyticsService } from './analytics.service';
import { BillingService } from './billing.service';
import { ExternalLinksService } from '../services/external-links.service';

export interface IapPlan {
  id: string;
  title: string;
  /** Localised, e.g. "£4.99" */
  price: string;
  period: 'month' | 'year' | 'other';
  pkg: PurchasesPackage;
}

/**
 * In-app purchases on the native builds (Apple 3.1.1 / Play Billing rail),
 * via RevenueCat. Off until REVENUECAT_IOS_KEY is set — then the account
 * sheet swaps the Stripe plan buttons for store packages. The RevenueCat
 * webhook writes entitlements/{uid} server-side, so the rest of the app
 * (gates, quotas, BillingService.isPremium) needs no idea where premium
 * came from. The SDK is configured with the Firebase uid as app_user_id.
 */
@Injectable({ providedIn: 'root' })
export class IapService {
  private readonly links = inject(ExternalLinksService);
  private readonly auth = inject(AuthService);
  private readonly billing = inject(BillingService);
  private readonly analytics = inject(AnalyticsService);

  readonly available = signal(IS_IOS_NATIVE && !!REVENUECAT_IOS_KEY);
  readonly plans = signal<IapPlan[]>([]);
  /** Localised prices for the consumable checks, keyed by product id. */
  readonly checkPrices = signal<Record<string, string>>({});
  readonly busy = signal(false);
  readonly error = signal('');
  /** Apple said yes; the entitlement is travelling RevenueCat → webhook → account (a few seconds). */
  readonly activating = signal(false);
  private configured = false;

  constructor() {
    // Keep RevenueCat's identity in step with Firebase's (sign-in links onto the anon uid).
    effect(() => {
      const uid = this.auth.uid();
      if (this.configured && uid) void Purchases.logIn({ appUserID: uid }).catch(() => undefined);
    });
  }

  /** Call once at app start (no-op on web / without a key). */
  async init(): Promise<void> {
    if (!this.available() || this.configured) return;
    try {
      await this.auth.ready;
      const uid = this.auth.uid();
      if (!uid) return;
      await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
      await Purchases.configure({ apiKey: REVENUECAT_IOS_KEY, appUserID: uid });
      this.configured = true;
      await Promise.all([this.loadPlans(), this.loadCheckPrices()]);
    } catch (e) {
      this.error.set((e as Error).message || 'Store unavailable right now.');
    }
  }

  private async loadPlans(): Promise<void> {
    const { current } = await Purchases.getOfferings();
    const packages = current?.availablePackages ?? [];
    this.plans.set(
      packages.map((p) => ({
        id: p.identifier,
        title: p.product.title,
        price: p.product.priceString,
        period: p.packageType === 'ANNUAL' ? 'year' : p.packageType === 'MONTHLY' ? 'month' : 'other',
        pkg: p,
      })),
    );
  }

  private async loadCheckPrices(): Promise<void> {
    try {
      const { products } = await Purchases.getProducts({ productIdentifiers: Object.values(IAP_CHECK_PRODUCTS) });
      const prices: Record<string, string> = {};
      for (const p of products) prices[p.identifier] = p.priceString;
      this.checkPrices.set(prices);
    } catch {
      /* prices stay unknown; buttons show without a figure */
    }
  }

  private checkProductId(kind: 'writeoff' | 'full'): string {
    if (kind === 'writeoff') return IAP_CHECK_PRODUCTS.writeoff;
    return this.billing.isPremium() ? IAP_CHECK_PRODUCTS.fullPremium : IAP_CHECK_PRODUCTS.full;
  }

  /** Display price for a one-off check, '' until the store answers. */
  checkPrice(kind: 'writeoff' | 'full'): string {
    return this.checkPrices()[this.checkProductId(kind)] ?? '';
  }

  /**
   * One-off check for ONE registration. The reg travels as a RevenueCat
   * subscriber attribute so the webhook can write the checkCredits doc the
   * check endpoints already honour.
   */
  async purchaseCheck(kind: 'writeoff' | 'full', registration: string): Promise<void> {
    const reg = registration.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!reg) return;
    this.busy.set(true);
    this.error.set('');
    this.analytics.track('checkout_started', { oneOff: true, channel: 'apple', kind });
    try {
      await Purchases.setAttributes({ check_reg: reg, check_kind: kind });
      const { products } = await Purchases.getProducts({ productIdentifiers: [this.checkProductId(kind)] });
      const product = products[0];
      if (!product) throw new Error('That check is not available in the App Store right now.');
      await Purchases.purchaseStoreProduct({ product });
      this.billing.pendingCheckReg.set(reg);
      this.billing.pendingCheckKind.set(kind);
      this.analytics.track('checkout_success', { kind: kind === 'full' ? 'full_check' : 'one_off_check', channel: 'apple' });
    } catch (e) {
      const err = e as { code?: string; message?: string };
      if (err.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
        this.analytics.track('checkout_cancelled', { oneOff: true, channel: 'apple' });
      } else {
        this.error.set(err.message || 'Purchase failed - nothing was charged.');
      }
    } finally {
      this.busy.set(false);
    }
  }

  async purchase(plan: IapPlan): Promise<void> {
    this.busy.set(true);
    this.error.set('');
    this.analytics.track('checkout_started', { channel: 'apple', plan: plan.period });
    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: plan.pkg });
      this.analytics.track('checkout_success', { kind: 'subscription', channel: 'apple' });
      await this.applyCustomerInfo(customerInfo);
    } catch (e) {
      const err = e as { code?: string; message?: string };
      if (err.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
        this.analytics.track('checkout_cancelled', { channel: 'apple' });
      } else {
        this.error.set(err.message || 'Purchase failed — nothing was charged.');
      }
    } finally {
      this.busy.set(false);
    }
  }

  /** Apple requires a visible way to restore purchases on a new device. */
  async restore(): Promise<void> {
    this.busy.set(true);
    this.error.set('');
    try {
      const { customerInfo } = await Purchases.restorePurchases();
      await this.applyCustomerInfo(customerInfo);
      this.analytics.track('iap_restore', { premium: this.billing.isPremium() });
      if (!this.billing.isPremium()) this.error.set('No active subscription found for this Apple ID.');
    } catch (e) {
      this.error.set((e as Error).message || 'Restore failed — please try again.');
    } finally {
      this.busy.set(false);
    }
  }

  /** Subscriptions are managed by the store, not by us. */
  manage(): void {
    void this.links.open('https://apps.apple.com/account/subscriptions');
  }

  /** Optimistic local grant; the webhook makes it durable within seconds. */
  /**
   * Apple has confirmed; show "activating" until the account record agrees
   * (webhook → Firestore, normally 3-8 s) so the rail never sits on the plan
   * buttons after a successful purchase (field test 2026-09-05).
   */
  private async applyCustomerInfo(info: CustomerInfo): Promise<void> {
    const ent = info.entitlements.active[IAP_ENTITLEMENT];
    if (ent) {
      const until = ent.expirationDate ? new Date(ent.expirationDate).getTime() : Date.now() + 35 * 86_400_000;
      this.billing.applyNativeEntitlement(until);
    }
    if (this.billing.isPremium()) return;
    this.activating.set(true);
    try {
      await this.billing.pollEntitlement();
      if (!this.billing.isPremium()) {
        this.error.set('Payment confirmed — Premium is still activating. It will show within a minute; reopen the app if not.');
      }
    } finally {
      this.activating.set(false);
    }
  }
}
